import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { 
  BarChart3, 
  DollarSign, 
  Users, 
  RefreshCw,
  Calendar,
  Download
} from 'lucide-react';
import { 
  PieChart, 
  Pie, 
  BarChart,
  Bar,
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend
} from 'recharts';
import { format } from 'date-fns';
import api, { endpoints } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';

const UBBReport = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [updatingSpend, setUpdatingSpend] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [backendLastUpdate, setBackendLastUpdate] = useState(null);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load available months for the filter
  const loadAvailableMonths = useCallback(async () => {
    try {
      // Use user-level endpoint if user is not admin
      const endpoint = user?.role === 'admin' ? endpoints.spend.summary : endpoints.spend.userSummary;
      const response = await api.get(endpoint);
      if (response.data?.success && response.data?.summary) {
        const months = response.data.summary.map(item => ({
          year: item.Year,
          month: item.Month,
          label: format(new Date(item.Year, item.Month - 1), 'MMMM yyyy'),
          userCount: item.UserCount,
          totalSpend: item.TotalSpend
        }));
        
        setAvailableMonths(months);
        
        // Set the most recent month as default
        if (months.length > 0) {
          setSelectedMonth({
            year: months[0].year,
            month: months[0].month
          });
        }
      }
    } catch (error) {
      console.error('Failed to load available months:', error);
      toast.error('Failed to load available months');
    }
  }, [user?.role]);

  // Fetch last update timestamp from backend
  const loadBackendLastUpdate = useCallback(async () => {
    try {
      const res = await api.get(endpoints.spend.lastUpdate);
      if (res.data?.success) {
        setBackendLastUpdate(res.data.lastUpdate ? new Date(res.data.lastUpdate) : null);
      }
    } catch (e) {
      // non-fatal
    }
  }, []);

  // Load report data for the selected month
  const loadReportData = useCallback(async () => {
    if (!selectedMonth) return;
    
    try {
      setLoading(true);
      
      const { year, month } = selectedMonth;
      
      // Use user-level endpoints if user is not admin
      const teamSummaryEndpoint = user?.role === 'admin' ? endpoints.spend.teamSummary : endpoints.spend.userTeamSummary;
      const historyEndpoint = user?.role === 'admin' ? endpoints.spend.history : endpoints.spend.userHistory;
      
      // Get team summary data
      const teamSummaryRes = await api.get(`${teamSummaryEndpoint}?year=${year}&month=${month}`);
      
      // Get individual user data
      const userDataRes = await api.get(`${historyEndpoint}?limit=500`);
      
      // Filter user data for the selected month/year
      const filteredUserData = userDataRes.data?.history?.filter(
        item => item.Year === year && item.Month === month
      ) || [];
      
      setReportData({
        teamSummary: teamSummaryRes.data?.teamSummary || [],
        userData: filteredUserData,
        year,
        month
      });
      
      // Do not set lastUpdate here; we only show backend timestamp on initial/refresh
      await loadBackendLastUpdate();
      
    } catch (error) {
      console.error('Failed to load UBB report data:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, user?.role, loadBackendLastUpdate]);

  // Update spend data for current month
  const updateSpendData = async () => {
    // Only admins can update spend data
    if (user?.role !== 'admin') {
      toast.error('Only administrators can update spend data');
      return;
    }
    
    try {
      setUpdatingSpend(true);
      toast.loading('Updating spend data...', { id: 'spend-update' });
      
      const response = await api.post(endpoints.spend.import);
      
      if (response.data.success) {
        toast.success('Spend data updated successfully!', { id: 'spend-update' });
        // Refresh the report data
        await loadReportData();
        // Update both: backend (in case background job updates table) and client-side fallback
        await loadBackendLastUpdate();
        setLastUpdate(new Date());
      } else {
        toast.error(response.data.message || 'Failed to update spend data', { id: 'spend-update' });
      }
    } catch (error) {
      console.error('Failed to update spend data:', error);
      toast.error('Failed to update spend data', { id: 'spend-update' });
    } finally {
      setUpdatingSpend(false);
    }
  };

  // Load data when component mounts or user role changes
  useEffect(() => {
    if (user) {
      loadAvailableMonths();
    }
  }, [user, loadAvailableMonths]);

  // Load report data when selected month changes
  useEffect(() => {
    if (selectedMonth) {
      loadReportData();
    }
  }, [selectedMonth, loadReportData]);

  // Helper function to get team display name (FriendlyName if available, otherwise TeamName)
  const getTeamDisplayName = (team) => {
    // Handle both team objects (with FriendlyName and TeamName properties) and string team names
    if (typeof team === 'string') {
      return team || 'Unknown';
    }
    return team?.FriendlyName || team?.TeamName || 'Unknown';
  };

  // Format team data for pie chart
  const getTeamPieChartData = () => {
    // If team summary is available and has data, use it
    if (reportData?.teamSummary && reportData.teamSummary.length > 0) {
      const totalSpend = reportData.teamSummary.reduce(
        (sum, team) => sum + (team.TotalSpend || 0), 0
      );
      
      return reportData.teamSummary
        .filter(team => (team.TotalSpend || 0) > 0) // Only include teams with spend > 0
        .map(team => ({
          name: getTeamDisplayName(team),
          value: team.TotalSpend || 0,
          percentage: totalSpend > 0 ? ((team.TotalSpend || 0) / totalSpend * 100).toFixed(1) : 0,
          userCount: team.UserCount || 0
        }));
    }
    
    // Fallback: aggregate user data by team
    if (reportData?.userData && reportData.userData.length > 0) {
      const teamAggregation = {};
      
      reportData.userData.forEach(user => {
        const teamName = getTeamDisplayName(user.TeamName);
        if (!teamAggregation[teamName]) {
          teamAggregation[teamName] = {
            totalSpend: 0,
            userCount: 0
          };
        }
        teamAggregation[teamName].totalSpend += user.TotalSpend || 0;
        teamAggregation[teamName].userCount += 1;
      });
      
      const totalSpend = Object.values(teamAggregation).reduce(
        (sum, team) => sum + team.totalSpend, 0
      );
      
      return Object.entries(teamAggregation)
        .filter(([teamName, data]) => data.totalSpend > 0) // Only include teams with spend > 0
        .map(([teamName, data]) => ({
          name: teamName,
          value: data.totalSpend,
          percentage: totalSpend > 0 ? ((data.totalSpend / totalSpend) * 100).toFixed(1) : 0,
          userCount: data.userCount
        }));
    }
    
    return [];
  };

  // Format user data for bar chart
  const getUserBarChartData = () => {
    if (!reportData?.userData) return [];
    
    // Sort users by spend in descending order and filter out users with $0 spend
    return [...reportData.userData]
      .filter(user => (user.TotalSpend || 0) > 0) // Filter out users with $0 spend
      .sort((a, b) => (b.TotalSpend || 0) - (a.TotalSpend || 0))
      .slice(0, 15) // Limit to top 15 users
      .map(user => ({
        name: user.Name || user.Email.split('@')[0] || 'Unknown',
        email: user.Email,
        team: getTeamDisplayName(user.TeamName),
        spend: user.TotalSpend || 0,
        requests: user.TotalRequests || 0
      }));
  };

  // Calculate optimal Y-axis width based on longest user name
  const getOptimalYAxisWidth = () => {
    const userData = getUserBarChartData();
    if (userData.length === 0) return isMobile ? 90 : 110;
    
    const longestName = Math.max(...userData.map(user => user.name.length));
    const baseWidth = isMobile ? 8 : 9; // pixels per character
    const minWidth = isMobile ? 80 : 100;
    const maxWidth = isMobile ? 120 : 140;
    
    const calculatedWidth = longestName * baseWidth;
    return Math.max(minWidth, Math.min(maxWidth, calculatedWidth));
  };

  // Chart colors
  const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f43f5e', '#84cc16', '#6366f1'];
  
  // Responsive chart heights
  const getChartHeight = (type = 'default') => {
    if (isMobile) {
      return type === 'main' ? 250 : 300;
    }
    return type === 'main' ? 350 : 400;
  };

  // Custom tooltip formatter for pie chart
  const teamTooltipFormatter = (value, name, props) => {
    const team = props.payload;
    return [
      `US$${value.toFixed(2)} (${team.percentage}%)`,
      `${name} - ${team.userCount} users`
    ];
  };

  if (loading && !reportData) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">
            UBB-Report
          </h1>
          <p className="text-secondary-600 mt-1">
            Track and analyze usage-based billing costs by team and user
          </p>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-4 lg:mt-0">
          <button
            onClick={loadReportData}
            disabled={loading || updatingSpend}
            className="btn-secondary btn-sm"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          
          <button
            onClick={updateSpendData}
            disabled={loading || updatingSpend}
            className="btn-primary btn-sm"
          >
            <Download className={`w-4 h-4 mr-2 ${updatingSpend ? 'animate-spin' : ''}`} />
            {updatingSpend ? 'Updating...' : 'Update'}
          </button>
        </div>
      </div>

      {/* Last Update Timestamp */}
      {(backendLastUpdate || lastUpdate) && (
        <div className="text-sm text-secondary-500 text-center">
          Last Update: {(backendLastUpdate || lastUpdate)?.toLocaleString()}
        </div>
      )}

      {/* Month Selector */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center">
              <Calendar className="w-5 h-5 text-secondary-600 mr-2" />
              <span className="text-secondary-700 font-medium">Select Month:</span>
            </div>
            
            <div className="flex-1">
              <select
                value={selectedMonth ? `${selectedMonth.year}-${selectedMonth.month}` : ''}
                onChange={(e) => {
                  if (e.target.value) {
                    const [year, month] = e.target.value.split('-').map(Number);
                    setSelectedMonth({ year, month });
                  }
                }}
                className="input w-full max-w-xs"
                disabled={loading || availableMonths.length === 0}
              >
                {availableMonths.length === 0 && (
                  <option value="">No data available</option>
                )}
                
                {availableMonths.map((item) => (
                  <option 
                    key={`${item.year}-${item.month}`} 
                    value={`${item.year}-${item.month}`}
                  >
                    {item.label} - ${item.totalSpend?.toFixed(2)} ({item.userCount} users)
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center h-32">
          <LoadingSpinner />
        </div>
      )}

      {!loading && reportData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="stat-value">
                    ${(() => {
                      // Calculate from both team summary and user data as fallback
                      const teamTotal = (reportData.teamSummary || []).reduce((sum, team) => sum + (team.TotalSpend || 0), 0);
                      const userTotal = (reportData.userData || []).reduce((sum, user) => sum + (user.TotalSpend || 0), 0);
                      return Math.max(teamTotal, userTotal).toFixed(2);
                    })()}
                  </div>
                  <div className="stat-label">Total UBB Spend (US$)</div>
                </div>
                <DollarSign className="w-8 h-8 text-primary-600" />
              </div>
            </div>

            <div className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="stat-value">
                    {(() => {
                      // Use user data count as primary source
                      const userCount = (reportData.userData || []).length;
                      const teamUserCount = (reportData.teamSummary || []).reduce((sum, team) => sum + (team.UserCount || 0), 0);
                      return Math.max(userCount, teamUserCount);
                    })()}
                  </div>
                  <div className="stat-label">Total Users</div>
                </div>
                <Users className="w-8 h-8 text-success-600" />
              </div>
            </div>

            <div className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="stat-value">
                    {(() => {
                      // Count unique teams from both sources
                      const teamSummaryCount = (reportData.teamSummary || []).length;
                      const uniqueTeamsFromUsers = new Set((reportData.userData || []).map(user => getTeamDisplayName(user.TeamName)).filter(Boolean)).size;
                      return Math.max(teamSummaryCount, uniqueTeamsFromUsers);
                    })()}
                  </div>
                  <div className="stat-label">Teams</div>
                </div>
                <BarChart3 className="w-8 h-8 text-warning-600" />
              </div>
            </div>
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Team Distribution Pie Chart */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-secondary-900">
                  Team Spend Distribution
                </h3>
                <p className="text-sm text-secondary-600">
                  Percentage breakdown of spending by team
                </p>
              </div>
              <div className="card-body">
                {getTeamPieChartData().length > 0 ? (
                  <ResponsiveContainer width="100%" height={getChartHeight()}>
                    <PieChart>
                      <Pie
                        data={getTeamPieChartData()}
                        cx="50%"
                        cy="50%"
                        outerRadius={isMobile ? 80 : 120}
                        dataKey="value"
                        nameKey="name"
                        label={({ name, percentage }) => `${name}: ${percentage}%`}
                        labelLine={true}
                      >
                        {getTeamPieChartData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={teamTooltipFormatter} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-64">
                    <p className="text-secondary-500">No team data available</p>
                  </div>
                )}
              </div>
            </div>

            {/* User Distribution Bar Chart */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-secondary-900">
                  Top Users by Spend
                </h3>
                <p className="text-sm text-secondary-600">
                  Individual user spending in descending order (users with $0 spend excluded)
                </p>
              </div>
              <div className="card-body">
                {getUserBarChartData().length > 0 ? (
                  <ResponsiveContainer width="100%" height={getChartHeight()}>
                    <BarChart
                      data={getUserBarChartData()}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: getOptimalYAxisWidth() + 10, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tickFormatter={(value) => `US$${value}`} />
                      <YAxis 
                        type="category" 
                        dataKey="name" 
                        width={getOptimalYAxisWidth()}
                        fontSize={isMobile ? 9 : 10}
                        interval={0}
                        tickFormatter={(value) => {
                          // Try to fit more text in single line, truncate if needed
                          const maxLength = isMobile ? 12 : 18;
                          if (value.length > maxLength) {
                            return `${value.substring(0, maxLength)}...`;
                          }
                          return value;
                        }}
                      />
                      <Tooltip 
                        formatter={(value, name) => [`US$${value.toFixed(2)}`, name]}
                        labelFormatter={(label) => {
                          const user = getUserBarChartData().find(u => u.name === label);
                          return `${user?.name} (${user?.team})`;
                        }}
                      />
                      <Legend />
                      <Bar 
                        dataKey="spend" 
                        name="Spend (US$)" 
                        fill="#3b82f6"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-64">
                    <p className="text-secondary-500">No user data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Team Breakdown Table */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-secondary-900">
                Team Breakdown
              </h3>
              <p className="text-sm text-secondary-600">
                Detailed spending by team
              </p>
            </div>
            <div className="card-body">
              <div className="overflow-x-auto">
                <table className="table min-w-full">
                  <thead>
                    <tr>
                      <th>Team</th>
                      <th>Users</th>
                      <th>Total Spend (US$)</th>
                      <th>Avg Spend/User (US$)</th>
                      <th>Total Requests</th>
                      <th>% of Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.teamSummary
                      .sort((a, b) => (b.TotalSpend || 0) - (a.TotalSpend || 0))
                      .map((team, index) => {
                        const totalSpend = reportData.teamSummary.reduce(
                          (sum, t) => sum + (t.TotalSpend || 0), 0
                        );
                        const percentage = totalSpend > 0 
                          ? ((team.TotalSpend || 0) / totalSpend * 100).toFixed(1) 
                          : 0;
                          
                        return (
                          <tr key={index}>
                            <td className="font-medium">{getTeamDisplayName(team)}</td>
                            <td>{team.UserCount || 0}</td>
                            <td className="font-semibold text-primary-600">
                              ${(team.TotalSpend || 0).toFixed(2)}
                            </td>
                            <td>
                              ${(team.AvgSpendPerUser || 0).toFixed(2)}
                            </td>
                            <td>{(team.TotalRequests || 0).toLocaleString()}</td>
                            <td>
                              <div className="flex items-center">
                                <div className="w-20 bg-secondary-200 rounded-full h-2 mr-2">
                                  <div 
                                    className="bg-primary-600 h-2 rounded-full" 
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                                <span className="text-sm text-secondary-600">
                                  {percentage}%
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UBBReport;
